import {
  BookOpen,
  Building2,
  CalendarDays,
  Contact,
  FileText,
  FolderCog,
  LayoutGrid,
  Library,
  ListChecks,
  MessageSquareWarning,
  RefreshCw,
  Send,
  Settings,
  Shield,
  Truck,
  UserCheck,
  type LucideIcon,
} from "lucide-react";
import type { AllowedRoute, NavBadgeScope, NavBadgeStyle } from "@/api/types";

// ── NAV-1 (LEFTNAV_Requirements_v0_3 §1/§2/§6 + BuildNotes v1, D-098/D-099) ──
// Frontend-canonical IA: routeKey → section / icon / canonical label + section
// and in-section order. Visibility / accessMode / labels (navLabelKey) / badge
// bindings come EXCLUSIVELY from backend `allowedRoutes` (/me) — this registry
// never decides who sees what (AC-NAV-OWNERSHIP, 0 role-inference).

export const SECTION_ORDER = [
  "Робота",
  "Клієнти",
  "Документи",
  "Підтримка",
  "Адміністрування",
] as const;

export type NavSection = (typeof SECTION_ORDER)[number];

interface NavRegistryEntry {
  section: NavSection;
  icon: LucideIcon;
  canonicalLabel: string;
  /** §0 placeholder marker chip (e.g. KB «у підготовці») — IA fact, not access. */
  placeholderNote?: string;
}

// Insertion order = canonical in-section order (§1 LOCKED IA).
const NAV_REGISTRY: Record<string, NavRegistryEntry> = {
  today: { section: "Робота", icon: LayoutGrid, canonicalLabel: "Сьогодні" },
  meetings: { section: "Робота", icon: CalendarDays, canonicalLabel: "Зустрічі" },
  tasks: { section: "Робота", icon: ListChecks, canonicalLabel: "Завдання" },
  requests: { section: "Робота", icon: Send, canonicalLabel: "Запити у відділи" },
  clients: { section: "Клієнти", icon: Building2, canonicalLabel: "Клієнти / Контрагенти" },
  contacts: { section: "Клієнти", icon: Contact, canonicalLabel: "Контакти" },
  profiles: { section: "Клієнти", icon: UserCheck, canonicalLabel: "Профілі клієнтів" },
  contracts: { section: "Документи", icon: FileText, canonicalLabel: "Договори" },
  complaints: { section: "Підтримка", icon: MessageSquareWarning, canonicalLabel: "Рекламації" },
  // D-147 S3 — Knowledge Library (/knowledge, allowedRoutes-gated P2: усі
  // active internal; видимість вирішує ВИКЛЮЧНО backend-emit).
  knowledge: { section: "Підтримка", icon: Library, canonicalLabel: "База знань" },
  // D-147 W1.1 — адмін-поверхня розділів. Ключ стоїть МІЖ knowledge і kb
  // свідомо: REGISTRY_ORDER = порядок ключів цього літерала, тож пункт
  // рендериться безпосередньо під «База знань» (а не під «AI-помічник»).
  // Видимість — виключно backend-emit (KB_Admin-grade), як і в усіх інших.
  knowledge_admin: { section: "Підтримка", icon: FolderCog, canonicalLabel: "Розділи бази знань" },
  // D-105 rename (D-147 S2): /kb = AI-помічник; «База знань» = Knowledge Library.
  kb: { section: "Підтримка", icon: BookOpen, canonicalLabel: "AI-помічник" },
  admin: { section: "Адміністрування", icon: Shield, canonicalLabel: "Адмін-центр" },
  admin_suppliers: { section: "Адміністрування", icon: Truck, canonicalLabel: "Постачальники" },
  sync_center: { section: "Адміністрування", icon: RefreshCw, canonicalLabel: "Синхро-центр" },
  settings: { section: "Адміністрування", icon: Settings, canonicalLabel: "Налаштування" },
};

const REGISTRY_ORDER = Object.keys(NAV_REGISTRY);

// navLabelKey → label (BuildNotes v1 lock). Unknown/absent key → canonical label
// (AC-NAV-LABEL-002 fallback comes from the registry: «Профілі клієнтів»).
const NAV_LABELS: Record<string, string> = {
  profile_fill: "Профілі на доповнення",
  profile_review: "Профілі на перевірку",
  profile_clients: "Профілі клієнтів",
};

export interface NavModelItem {
  routeKey: string;
  /** Click target — route + ?scope=team when badgeScope=team (AC-NAV-BADGE-SCOPE). */
  to: string;
  /** Pathname for active matching (without the scope query). */
  route: string;
  label: string;
  icon: LucideIcon;
  readOnly: boolean;
  badgeBinding?: string;
  badgeScope?: NavBadgeScope;
  badgeStyle?: NavBadgeStyle;
  placeholderNote?: string;
}

export interface NavModelSection {
  section: NavSection;
  items: NavModelItem[];
}

/**
 * Merge backend allowedRoutes with the canonical registry into the render model.
 * - presence + accessMode≠hidden = visible (omit-based contract, AC-NAV-ALLOWEDROUTES-002)
 * - unknown routeKey → skipped (no canonical IA slot — never invent an item)
 * - section with 0 visible items → not emitted (§7 empty-section rule)
 */
export function buildNavModel(allowedRoutes: AllowedRoute[]): NavModelSection[] {
  const byKey = new Map<string, AllowedRoute>();
  for (const r of allowedRoutes) {
    if (r.accessMode !== "hidden") byKey.set(r.routeKey, r);
  }

  const sections = new Map<NavSection, NavModelItem[]>();
  for (const routeKey of REGISTRY_ORDER) {
    const allowed = byKey.get(routeKey);
    if (!allowed) continue;
    const reg = NAV_REGISTRY[routeKey];
    const label =
      (allowed.navLabelKey && NAV_LABELS[allowed.navLabelKey]) || reg.canonicalLabel;
    const teamFiltered = allowed.badgeScope === "team";
    const item: NavModelItem = {
      routeKey,
      route: allowed.route,
      to: teamFiltered ? `${allowed.route}?scope=team` : allowed.route,
      label,
      icon: reg.icon,
      readOnly: allowed.accessMode === "readOnly",
      badgeBinding: allowed.badgeBinding,
      badgeScope: allowed.badgeScope,
      badgeStyle: allowed.badgeStyle,
      placeholderNote: reg.placeholderNote,
    };
    if (!sections.has(reg.section)) sections.set(reg.section, []);
    sections.get(reg.section)!.push(item);
  }

  return SECTION_ORDER.filter((s) => sections.has(s)).map((section) => ({
    section,
    items: sections.get(section)!,
  }));
}

/** Route owns the pathname when it IS the pathname or is a path-segment prefix of it. */
function routeMatches(pathname: string, route: string): boolean {
  const p = pathname.toLowerCase().replace(/\/+$/, "") || "/";
  const r = route.toLowerCase().replace(/\/+$/, "") || "/";
  return p === r || p.startsWith(r === "/" ? "/" : `${r}/`);
}

/**
 * LONGEST-ROUTE-WINS active resolution (AC-W11-ACTIVE-ROUTE).
 *
 * NavLink's default matching is prefix-based, so nested nav pairs light up BOTH
 * items: /knowledge/admin activated «База знань» + «Розділи бази знань», and
 * /admin/suppliers activated «Адмін-центр» + «Постачальники». Plain `end` would
 * over-correct — /knowledge/sections/:id must keep «База знань» lit.
 *
 * So: among all VISIBLE nav routes matching the pathname, exactly the longest
 * one wins. Generic over the registry — no per-feature hardcode; a future nested
 * pair is covered automatically. Returns null when nothing matches.
 */
export function navActiveRoute(sections: NavModelSection[], pathname: string): string | null {
  let winner: string | null = null;

  for (const { items } of sections) {
    for (const item of items) {
      if (!routeMatches(pathname, item.route)) continue;
      if (winner === null || item.route.length > winner.length) winner = item.route;
    }
  }

  return winner;
}
